import { useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import {
  NAV_GROUPS,
  SECTIONS,
  grantable,
  paths,
  roleDefault,
  type AccessPolicy,
  type AppSection,
  type Role,
} from "@clevis/shared-types";
import { Button, Card, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { CompanyMenuEditor } from "../../components/CompanyMenuEditor";

// The owner decides who sees what: tick a whole group, an item, or the
// sub-items under it — per role, then per person on top of their role.
// Saved on the server, which re-issues everyone's sign-in so the menu, the
// pages and the data rules all follow it.

const ROLES: Array<[Exclude<Role, "owner">, string]> = [
  ["admin", "Admin"],
  ["manager", "Manager"],
  ["staff", "Office staff"],
  ["tech", "Field tech"],
];
const ROLE_LABEL: Record<Role, string> = { owner: "Owner", admin: "Admin", manager: "Manager", staff: "Office staff", tech: "Field tech" };

type Target = { kind: "role"; role: Exclude<Role, "owner"> } | { kind: "person"; uid: string };
type Member = { uid: string; name: string; role: Role };

function TriCheckbox({ checked, mixed, disabled, onChange, label }: { checked: boolean; mixed: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  }, [mixed]);
  return <input ref={ref} type="checkbox" aria-label={label} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />;
}

export function AccessPage() {
  const { tenantId } = useAuth();
  const [policy, setPolicy] = useState<Partial<AccessPolicy>>({});
  const [members, setMembers] = useState<Member[]>([]);
  const [target, setTarget] = useState<Target>({ kind: "role", role: "admin" });
  const [draft, setDraft] = useState<Record<string, boolean> | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(doc(db, paths.tenantAccessPolicy(tenantId)), (snap) => setPolicy((snap.data() as Partial<AccessPolicy>) ?? {})),
      onSnapshot(collection(db, paths.tenantMembers(tenantId)), (snap) =>
        setMembers(
          snap.docs
            .filter((d) => d.get("disabled") !== true)
            .map((d) => ({ uid: d.id, name: (d.get("displayName") as string) || (d.get("email") as string) || "Teammate", role: d.get("role") as Role }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        ),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  const person = target.kind === "person" ? members.find((m) => m.uid === target.uid) : undefined;
  const role: Role = target.kind === "role" ? target.role : (person?.role ?? "staff");

  // What the role has (defaults + owner's role settings), and the saved state for the target.
  const roleOn = useMemo(() => {
    const r = policy.roles?.[role] ?? {};
    return (id: string) => (id in r ? r[id]! : roleDefault(role, id));
  }, [policy, role]);
  const saved = useMemo(() => {
    const out: Record<string, boolean> = {};
    const u = target.kind === "person" ? (policy.users?.[target.uid] ?? {}) : {};
    for (const s of SECTIONS) out[s.id] = s.id in u ? u[s.id]! : roleOn(s.id);
    return out;
  }, [policy, target, roleOn]);
  const state = draft ?? saved;
  useEffect(() => setDraft(null), [target]);

  const allowedFor = (s: AppSection) => roleDefault(role, s.id) || grantable(role, s.id);
  const set = (ids: string[], on: boolean) =>
    setDraft((d) => {
      const next = { ...(d ?? saved) };
      for (const id of ids) {
        const s = SECTIONS.find((x) => x.id === id)!;
        if (on && !allowedFor(s)) continue;
        next[id] = on;
        // Turning an item off takes its sub-items with it.
        if (!on) for (const sub of SECTIONS.filter((x) => x.parent === id)) next[sub.id] = false;
      }
      return next;
    });

  async function save() {
    if (!tenantId) return;
    setBusy(true);
    setNote("");
    try {
      const res = await call<{ refreshed: number }>(
        "setAccessPolicy",
        target.kind === "role" ? { tenantId, role: { role: target.role, sections: state } } : { tenantId, person: { uid: target.uid, sections: state } },
        { timeoutMs: 300_000 },
      );
      setDraft(null);
      setNote(`Saved. ${res.refreshed} ${res.refreshed === 1 ? "person's" : "people's"} access updated; their menu changes within seconds.`);
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function resetPerson() {
    if (!tenantId || target.kind !== "person") return;
    setBusy(true);
    try {
      await call("setAccessPolicy", { tenantId, person: { uid: target.uid, sections: null } }, { timeoutMs: 300_000 });
      setDraft(null);
      setNote("Back to their role's access.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const custom = (id: string) => target.kind === "person" && state[id] !== roleOn(id);
  const hasCustom = target.kind === "person" && Object.keys(policy.users?.[target.uid] ?? {}).length > 0;
  const dirty = draft !== null && SECTIONS.some((s) => draft[s.id] !== saved[s.id]);
  const value = target.kind === "role" ? `role:${target.role}` : `person:${target.uid}`;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-lg font-bold">Roles &amp; access</h1>
        <p className="text-xs text-steel">
          Choose what each role sees in the menu, then fine-tune any person. Turning something off hides it and blocks
          it on the server too, so a typed-in link won't open it. As the owner you always see everything.
        </p>
      </div>

      <Card className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ink">Set access for</span>
          <Select
            className="w-auto"
            value={value}
            onChange={(e) => {
              const [kind, id] = e.target.value.split(":") as [string, string];
              setTarget(kind === "role" ? { kind: "role", role: id as Exclude<Role, "owner"> } : { kind: "person", uid: id });
            }}
          >
            <optgroup label="Roles">
              {ROLES.map(([r, label]) => (
                <option key={r} value={`role:${r}`}>
                  Every {label}
                </option>
              ))}
            </optgroup>
            <optgroup label="People">
              {members
                .filter((m) => m.role !== "owner")
                .map((m) => (
                  <option key={m.uid} value={`person:${m.uid}`}>
                    {m.name} ({ROLE_LABEL[m.role]})
                  </option>
                ))}
            </optgroup>
          </Select>
          {person ? <span className="text-xs text-steel">Starts from the {ROLE_LABEL[person.role]} role; changes here are just for them.</span> : null}
        </div>

        <div className="space-y-3">
          {NAV_GROUPS.map((group) => {
            const items = SECTIONS.filter((s) => s.group === group && !s.parent);
            const ids = SECTIONS.filter((s) => s.group === group).map((s) => s.id);
            const usable = SECTIONS.filter((s) => s.group === group && allowedFor(s));
            const onCount = usable.filter((s) => state[s.id]).length;
            return (
              <div key={group} className="rounded-lg border border-steel/20">
                <label className="flex items-center gap-2 bg-cream/60 px-3 py-1.5 text-sm font-semibold text-ink">
                  <TriCheckbox
                    label={`All of ${group}`}
                    checked={usable.length > 0 && onCount === usable.length}
                    mixed={onCount > 0 && onCount < usable.length}
                    disabled={usable.length === 0}
                    onChange={(v) => set(ids, v)}
                  />
                  {group}
                </label>
                <div className="divide-y divide-steel/10">
                  {items.map((s) => {
                    const subs = SECTIONS.filter((x) => x.parent === s.id);
                    const ok = allowedFor(s);
                    return (
                      <div key={s.id} className="px-3 py-1">
                        <label className={cn("flex items-center gap-2 text-sm", !ok && "opacity-50")}>
                          <input type="checkbox" checked={!!state[s.id] && ok} disabled={!ok} onChange={(e) => set([s.id], e.target.checked)} />
                          <span className="text-ink">{s.label}</span>
                          {custom(s.id) ? <span className="rounded bg-brandorange/15 px-1 text-[10px] text-brandorange-dark">custom</span> : null}
                          {!ok ? (
                            <span className="text-[11px] text-steel">{s.ownerOnly ? "owner only" : "office page; field techs can't have it"}</span>
                          ) : null}
                        </label>
                        {subs.map((sub) => {
                          const subOk = allowedFor(sub) && !!state[s.id];
                          return (
                            <label key={sub.id} className={cn("ml-6 flex items-center gap-2 text-xs", !subOk && "opacity-50")}>
                              <input type="checkbox" checked={!!state[sub.id] && subOk} disabled={!subOk} onChange={(e) => set([sub.id], e.target.checked)} />
                              <span className="text-ink">{sub.label}</span>
                              {custom(sub.id) ? <span className="rounded bg-brandorange/15 px-1 text-[10px] text-brandorange-dark">custom</span> : null}
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button busy={busy} disabled={!dirty} onClick={() => void save()}>
            Save {target.kind === "role" ? `for every ${ROLE_LABEL[role]}` : `for ${person?.name ?? "this person"}`}
          </Button>
          {dirty ? (
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Undo changes
            </Button>
          ) : null}
          {hasCustom ? (
            <Button variant="ghost" busy={busy} onClick={() => void resetPerson()}>
              Reset to their role
            </Button>
          ) : null}
          {note ? <span className="text-xs text-steel">{note}</span> : null}
        </div>
      </Card>

      {tenantId ? <CompanyMenuEditor tenantId={tenantId} /> : null}
    </div>
  );
}
